import {
    ACTIVITIES,
    AGENDA,
    COMPANIES,
    CONTACTS,
    OPPORTUNITIES,
    SALESORDERS,
    TASKS,
} from 'constants/Entities';
import { AGENDA_EVENT, AGENDA_TASK } from 'constants/Constants';
import Context from 'managers/Context';
import { ACTIVITY_TYPE_ANNOTATION } from 'models/ActivityModel';
import { getPreviousAndNextDate } from 'utils/dates';
import { getActiveCrud } from 'utils/crud';
import { translateObjectB4ToB2 } from 'utils/fm/fields';
import { getLiteral } from 'utils/getLiteral';
import { COPILOT_ACTION_TYPES, COPILOT_ENTITY_TYPE_MAP } from '../constants';
import { toCopilotModifiedFieldsMap } from '../copilotModifiedFields';
import { COPILOT_TRANSFORMERS } from '../transformers';

/**
 * Helper function to check if user has create permission for an entity
 */
const hasCreatePermission = (entity) => {
    const state = Context.store.getState();
    const crudPermissions = state?.config?.permission?.crud_permission;
    return crudPermissions?.[entity.permission]?.create || false;
};

/**
 * Helper function to get user data
 */
const getUserData = () => {
    const state = Context.store.getState();
    const userData = state.config.userData || null;
    return {
        userId: userData?.idUsuario || '',
        idTimezone: userData?.idTimeZone || '',
        userName: userData ? `${userData.nombre} ${userData.apellidos}` : '',
    };
};

const withCopilotProvenance = (config, transformedData) => ({
    ...config,
    copilotModifiedFields: toCopilotModifiedFieldsMap(transformedData),
    copilotProductLines: Array.isArray(transformedData?.products)
        ? transformedData.products
        : undefined,
});

export const getModalConfig = (entityType, params = {}) => {
    const { form } = params;
    const { userId, userName, idTimezone } = getUserData();
    const { configMap, mappedSchema } = getActiveCrud();

    if (!mappedSchema) return null;

    // Translate B4 data to B2 format (filters out unmapped B4 fields automatically)
    let finalData = translateObjectB4ToB2(COPILOT_ENTITY_TYPE_MAP[entityType], form, {
        configMap,
        mappedSchema,
    });

    // Apply copilot-specific transformations using Model methods
    const transformer = COPILOT_TRANSFORMERS[entityType];
    if (transformer) finalData = transformer(finalData);

    switch (entityType) {
        case COMPANIES.trueName:
            return withCopilotProvenance(
                {
                    entity: COMPANIES,
                    data: finalData,
                    labels: {
                        title: getLiteral('page_title_accounts_create'),
                        success: getLiteral('succes_entitycreatedsuccessfully'),
                        error: getLiteral('label_failed_create'),
                        middleButtonText: getLiteral('action_save_and_detail'),
                    },
                    hasMiddleButton: true,
                    refreshIf: [COMPANIES],
                    isCreateGlobal: true,
                },
                finalData,
            );

        case CONTACTS.trueName:
            return withCopilotProvenance(
                {
                    entity: CONTACTS,
                    data: finalData,
                    labels: {
                        title: getLiteral('page_title_contacts_create'),
                        success: getLiteral('succes_entitycreatedsuccessfully'),
                        error: getLiteral('label_failed_create'),
                        middleButtonText: getLiteral('action_save_and_detail'),
                    },
                    showDelete: false,
                    hasMiddleButton: true,
                    refreshIf: [CONTACTS],
                    isCreateGlobal: true,
                },
                finalData,
            );

        case ACTIVITIES.trueName:
            return withCopilotProvenance(
                {
                    entity: ACTIVITIES,
                    data: finalData,
                    labels: {
                        title: getLiteral('title_add_activity'),
                        success: getLiteral('succes_entitycreatedsuccessfully'),
                        error: getLiteral('label_failed_create'),
                        middleButtonText: getLiteral('action_save_and_detail'),
                    },
                    extraInfo: {
                        activityType: ACTIVITY_TYPE_ANNOTATION.toString(),
                    },
                    hasMiddleButton: true,
                    refreshIf: [ACTIVITIES],
                    isCreateGlobal: true,
                },
                finalData,
            );

        case OPPORTUNITIES.trueName:
            return withCopilotProvenance(
                {
                    entity: OPPORTUNITIES,
                    data: finalData,
                    labels: {
                        title: getLiteral('page_title_opportunities_create'),
                        success: getLiteral('succes_entitycreatedsuccessfully'),
                        error: getLiteral('label_failed_create'),
                        middleButtonText: getLiteral('action_save_and_detail'),
                    },
                    hasMiddleButton: true,
                    refreshIf: [OPPORTUNITIES],
                    isCreateGlobal: true,
                },
                finalData,
            );

        case SALESORDERS.trueName:
            return withCopilotProvenance(
                {
                    entity: SALESORDERS,
                    data: finalData,
                    labels: {
                        title: getLiteral('title_add_salesorder'),
                        success: getLiteral('succes_entitycreatedsuccessfully'),
                        error: getLiteral('label_failed_create'),
                        middleButtonText: getLiteral('action_save_and_detail'),
                    },
                    hasMiddleButton: true,
                    refreshIf: [SALESORDERS],
                    isCreateGlobal: true,
                    size: 'big',
                },
                finalData,
            );

        case AGENDA.trueName: {
            const defaultRange = getPreviousAndNextDate(60, finalData.hini);
            const hini = defaultRange.previous;
            const fini = new Date(hini);
            const hfin = defaultRange.next;

            return withCopilotProvenance(
                {
                    entity: AGENDA,
                    data: {
                        ...finalData,
                        user: {
                            label: userName,
                            value: userId,
                        },
                        fini,
                        hini,
                        hfin,
                        isCompletado: '0',
                        isTarea: '0',
                        idTimezone,
                    },
                    labels: {
                        title: getLiteral('title_create_event'),
                        success: getLiteral('succes_entitycreatedsuccessfully'),
                        error: getLiteral('label_failed_create'),
                        middleButtonText: getLiteral('action_save_and_list'),
                    },
                    hideDelete: true,
                    crudTab: AGENDA_EVENT,
                    forceRefresh: true,
                    hasMiddleButton: true,
                    refreshIf: [AGENDA, TASKS],
                    isCreateGlobal: true,
                },
                finalData,
            );
        }

        case TASKS.trueName: {
            const defaultRange = getPreviousAndNextDate(60, finalData.hini);
            const hini = defaultRange.previous;
            const fini = new Date(hini);

            return withCopilotProvenance(
                {
                    entity: TASKS,
                    data: {
                        ...finalData,
                        user: {
                            label: userName,
                            value: userId,
                        },
                        fini,
                        hini,
                        isCompletado: '0',
                        isTarea: '1',
                        idTimezone,
                    },
                    labels: {
                        title: getLiteral('action_create_task'),
                        success: getLiteral('succes_entitycreatedsuccessfully'),
                        error: getLiteral('label_failed_create'),
                        middleButtonText: getLiteral('action_save_and_list'),
                    },
                    hideDelete: true,
                    crudTab: AGENDA_TASK,
                    hasMiddleButton: true,
                    refreshIf: [AGENDA, TASKS],
                    isCreateGlobal: true,
                },
                finalData,
            );
        }

        default:
            return null;
    }
};

/**
 * Get CTA text based on entity type
 */
const getCtaText = (entityType) => {
    const entity = COPILOT_ENTITY_TYPE_MAP[entityType];
    const entityName = entity?.labels?.singular
        ? getLiteral(entity.labels.singular).toLowerCase()
        : 'entity';
    return `Review and create ${entityName}`;
};

/**
 * Create Entity Modal Action
 * Opens a creation modal for the specified entity type
 */
export const createEntityModalAction = {
    key: COPILOT_ACTION_TYPES.ENTITY_FORM,
    definition: {
        getCta: (params = {}) => {
            const { entity } = params;
            return getCtaText(entity);
        },
        action: (params = {}) => {
            const { entity, ...restParams } = params;

            if (!entity) {
                console.warn('createEntityModal: entity parameter is required');
                return false;
            }

            const entityConstant = COPILOT_ENTITY_TYPE_MAP[entity];
            if (!entityConstant) {
                console.warn(
                    `createEntityModal: Unknown entity type '${entity}'. Supported: accounts, activities, contacts, opportunities, salesorders, calendar, task`,
                );
                return false;
            }

            // Check permissions
            if (!hasCreatePermission(entityConstant)) {
                console.warn(
                    `createEntityModal: User does not have permission to create ${entity}`,
                );
                return false;
            }

            // Special check for salesorders
            if (entity === 'salesorders') {
                const state = Context.store.getState();
                const allowSalesOrders = state?.config?.permission?.salesOrders;
                if (!allowSalesOrders) {
                    console.warn('createEntityModal: Sales orders feature is not enabled');
                    return false;
                }
            }

            // Get modal configuration
            const modalConfig = getModalConfig(entity, restParams);
            if (!modalConfig) {
                console.warn(`createEntityModal: Could not generate modal config for ${entity}`);
                return false;
            }

            // Dispatch modal init action
            const { dispatch } = Context.store;
            const { EntityModalActions } = Context.actions;

            dispatch(EntityModalActions.init(modalConfig));

            return true;
        },
    },
};
